import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import PropTypes from 'prop-types';
import { FlyoutHeader } from 'components/common/omnia/flyout-component';
import { FormattedMessage } from 'react-intl';
import { toggleChatBotPanel } from 'store/actions/panel-controller-actions';
import { ReactComponent as Information } from 'icons/icon-information-tooltip.svg';
import { ReactComponent as GlobeIcon } from 'icons/chatbot-globe.svg';
import { ReactComponent as CheckmarkIcon } from 'icons/checkmarkDropdown.svg';
import { useIntl } from 'react-intl';
import ReactDOM from 'react-dom';
import Button, { BUTTON_TYPES } from 'components/common/button-component';
import useOutsideClick from 'components/hooks/useOutsideClick';
import classNames from 'classnames';
import MultiLineEllipsis from 'components/common/multi-line-ellipsis-text-component';

export const CHATBOT_PANEL_BLOCK = 'chatbot-panel';
export const CHATBOT_PANEL_BLOCK_ID = 'chatbot-panel-id';
export const ICON_SIZE = '18px';
const CLOSE_ICON_DIMENSION_SIZE = '20';

export const ChatBotPanelHeader = ({
  onCancelPanel,
  setShowSecondaryMessage,
  setClearSessionModal,
  setIndexingCompleted,
  setText,
  messages,
  languageData = [],
  selectedLanguage,
  onLanguageSelect,
}) => {
  const intl = useIntl();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [dropdownRef] = useOutsideClick(() => setIsLanguageDropdownOpen(false));

  const handleLanguageToggle = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const selectedLanguageOption = languageData.find(lang => lang.value === selectedLanguage);
  const selectedLanguageDisplay = selectedLanguageOption 
    ? `${selectedLanguageOption.title} (${selectedLanguageOption.value.toUpperCase()})` 
    : '';

  const handleLanguageSelect = (language) => {
    onLanguageSelect(language);
    setIsLanguageDropdownOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsLanguageDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isLanguageDropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLanguageDropdownOpen]);

  const tooltipContent = () => {
    const mainTitle = intl.formatMessage({
      id: 'chatbot.header.tooltip.main.header',
    });
    const purposeTitle = intl.formatMessage({
      id: 'chatbot.header.tooltip.purpose.header',
    });
    const purposeDescription = intl.formatMessage({
      id: 'chatbot.header.tooltip.purpose.description',
    });
    const internalUseTitle = intl.formatMessage({
      id: 'chatbot.header.tooltip.internal.header',
    });
    const internalUseDescription = intl.formatMessage({
      id: 'chatbot.header.tooltip.internal.description',
    });
    const servicesTitle = intl.formatMessage({
      id: 'chatbot.header.tooltip.services.header',
    });
    const servicesDescription1 = intl.formatMessage({
      id: 'chatbot.header.tooltip.services.description.one',
    });
    const servicesDescription2 = intl.formatMessage({
      id: 'chatbot.header.tooltip.services.description.two',
    });
    const servicesDescription3 = intl.formatMessage({
      id: 'chatbot.header.tooltip.services.description.three',
    });
    const servicesDescription4 = intl.formatMessage({
      id: 'chatbot.header.tooltip.services.description.four',
    });
    const servicesDescription5 = intl.formatMessage({
      id: 'chatbot.header.tooltip.services.description.five',
    });
    const outputTitle = intl.formatMessage({
      id: 'chatbot.header.tooltip.output.header',
    });
    const outputDescription = intl.formatMessage({
      id: 'chatbot.header.tooltip.output.description',
    });
    return (
      <div className="tooltip-text-container">
        <div className="main-title">{mainTitle}</div>
        <div className="title-description">
          <div className="section">
            <div className="heading">{purposeTitle}</div>
            <div>{purposeDescription}</div>
          </div>
          <div className="section">
            <div className="heading">{internalUseTitle}</div>
            <p>{internalUseDescription}</p>
          </div>
          <div className="section">
            <div className="heading">{servicesTitle}</div>
            <ul>
              <li>{servicesDescription1}</li>
              <li>{servicesDescription2}</li>
              <li>{servicesDescription3}</li>
              <li>{servicesDescription4}</li>
              <li>{servicesDescription5}</li>
            </ul>
          </div>
          <div className="section">
            <div className="heading">{outputTitle}</div>
            <p>{outputDescription}</p>
          </div>
        </div>
      </div>
    );
  };

  const onCloseChatBotPanel = () => {
    onCancelPanel();
    if (messages?.length > 0) {
      setShowSecondaryMessage && setShowSecondaryMessage(true);
    } else {
      setShowSecondaryMessage && setShowSecondaryMessage(false);
    }
    setIndexingCompleted && setIndexingCompleted(true);
    setText && setText('');
  };

  return (
    <FlyoutHeader
      enableClose
      onClose={onCloseChatBotPanel}
      CLOSE_ICON_DIMENSION={CLOSE_ICON_DIMENSION_SIZE}
    >
      <div className={`${CHATBOT_PANEL_BLOCK}__header`}>
        <h3 className={`${CHATBOT_PANEL_BLOCK}__title`}>
          <FormattedMessage id={'chatbot-panel.title'} />
          <>
            <Information
              id="chatbot-header-info-icon"
              name={'information'}
              className={`${CHATBOT_PANEL_BLOCK}__info_icon`}
              width={ICON_SIZE}
              height={ICON_SIZE}
            />
            {ReactDOM.createPortal(
              <ReactTooltip
                className={'chatbot-tooltip'}
                anchorSelect="#chatbot-header-info-icon"
                place="right"
              >
                {tooltipContent()}
              </ReactTooltip>,
              document.getElementById('tooltip-root'),
            )}
          </>
        </h3>
        <div className={`${CHATBOT_PANEL_BLOCK}__header-actions`}>
          <div className={`${CHATBOT_PANEL_BLOCK}__language-selector`} ref={dropdownRef}>
            {selectedLanguageDisplay && (
              <div className={`${CHATBOT_PANEL_BLOCK}__selected-language-text`}>
                <MultiLineEllipsis
                  text={selectedLanguageDisplay}
                  toolTipID={`${CHATBOT_PANEL_BLOCK_ID}-selected-language-tooltip`}
                  tooltipPosition="bottom"
                  isNotInternationalized
                  maxLines={1}
                />
              </div>
            )}
            <button
              id={`${CHATBOT_PANEL_BLOCK_ID}-language-toggle`}
              className={classNames(
                `${CHATBOT_PANEL_BLOCK}__language-toggle`,
                {
                  [`${CHATBOT_PANEL_BLOCK}__language-toggle--open`]: isLanguageDropdownOpen,
                }
              )}
              onClick={handleLanguageToggle}
            >
              <GlobeIcon
                className={`${CHATBOT_PANEL_BLOCK}__language-icon`}
                width={ICON_SIZE}
                height={ICON_SIZE}
              />
            </button>
            
            {isLanguageDropdownOpen && (
              <div
                className={`${CHATBOT_PANEL_BLOCK}__language-dropdown`}
              >
                <div className={`${CHATBOT_PANEL_BLOCK}__language-dropdown-content`}>
                  {languageData?.length > 0 ? (
                    languageData.map((language, index) => {
                      const isLabelOption = language?.isLabel || language?.value === 'statement-create.statement-info.language-other-option';
                      
                      return isLabelOption ? (
                        <div
                          key={language?.value || index}
                          className={classNames(
                            `${CHATBOT_PANEL_BLOCK}__language-option`,
                            `${CHATBOT_PANEL_BLOCK}__language-option--label`
                          )}
                        >
                          <span className={`${CHATBOT_PANEL_BLOCK}__language-option-text`}>
                            {language?.isIntl ? (
                              <FormattedMessage id={language?.title} />
                            ) : (
                              language?.title
                            )}
                          </span>
                        </div>
                      ) : (
                        <button
                          key={language?.value || index}
                          className={classNames(
                            `${CHATBOT_PANEL_BLOCK}__language-option`,
                            {
                              [`${CHATBOT_PANEL_BLOCK}__language-option--selected`]:
                                language?.value === selectedLanguage,
                            }
                          )}
                          onClick={() => handleLanguageSelect(language)}
                        >
                          <span className={`${CHATBOT_PANEL_BLOCK}__language-option-text`}>
                            {language?.isIntl ? (
                              <FormattedMessage id={language?.title} />
                            ) : (
                              `${language?.title} (${language?.value.toUpperCase()})`
                            )}
                          </span>
                          {language?.value === selectedLanguage && (
                            <CheckmarkIcon 
                              className={`${CHATBOT_PANEL_BLOCK}__language-checkmark`}
                              width="16"
                              height="16"
                            />
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className={`${CHATBOT_PANEL_BLOCK}__language-no-options`}>
                      No languages available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <Button
            id={`${CHATBOT_PANEL_BLOCK_ID}-clear-session`}
            className={`${CHATBOT_PANEL_BLOCK}__clear-session-btn`}
            type={BUTTON_TYPES.tertiary}
            onClick={() => setClearSessionModal(true)}
            isTooltipRequiredForDisabled
          >
            <FormattedMessage id={'chatbot-input.clearSession'} />
          </Button>
        </div>
      </div>
    </FlyoutHeader>
  );
};

ChatBotPanelHeader.propTypes = {
  // action method fired to close toolkit-export flyout panel
  onCancelPanel: PropTypes.func.isRequired,
  // array of available languages
  languageData: PropTypes.arrayOf(
    PropTypes.shape({
      languageCode: PropTypes.string.isRequired,
      languageName: PropTypes.string,
      englishName: PropTypes.string,
    }),
  ),
  // currently selected language code
  selectedLanguage: PropTypes.string,
  // function called when a language is selected
  onLanguageSelect: PropTypes.func,
  // messages array for determining secondary message state
  messages: PropTypes.array,
  // function to set secondary message state
  setShowSecondaryMessage: PropTypes.func,
  // function to set indexing completed state
  setIndexingCompleted: PropTypes.func,
  // function to set text state
  setText: PropTypes.func,
  // function to set clear session modal state
  setClearSessionModal: PropTypes.func,
};

const mapDispatchToProps = {
  onCancelPanel: toggleChatBotPanel,
};

export default connect(null, mapDispatchToProps)(ChatBotPanelHeader);
